import router from '@/router'
import { useUserStore } from '@/stores/user'

/**
 * True when the current user is a vector (super) admin rather than a provider_admin.
 * Vector admins have no `access.provider` of their own — they act on providers via
 * the currently-routed provider_uuid instead (see resolveProviderUuid).
 */
export function isVectorAdmin(): boolean {
  return !!useUserStore().data?.access?.vector
}

/**
 * Resolves the provider UUID the current view is scoped to.
 *
 * Prefers the route's `provider_uuid` param — this is what lets vector admins act on
 * an arbitrary provider, mirroring the `X-Provider-UUID` header the axios interceptor
 * already injects from this same param (see src/plugins/axios.ts). Falls back to the
 * store's explicit active provider (populated from GET /me/providers — see
 * src/stores/user.ts) for provider users on routes that don't carry the param. A
 * provider user can belong to more than one provider organization; the active one is
 * whichever was last set via PATCH /me/active-provider (see SelectProvider.vue and the
 * UserProfile.vue switcher), persisted server-side.
 *
 * Reads `uuid`, never `provider_id` — the latter is the internal bigint PK and does
 * not resolve against the `/api/providers/{provider}` route binding.
 */
export function resolveProviderUuid(): string | null {
  const fromRoute = router.currentRoute.value.params.provider_uuid
  if (typeof fromRoute === 'string' && fromRoute)
    return fromRoute

  return useUserStore().activeProvider?.uuid ?? null
}
